import {
  FEMA_CONTEXT_RECEIPT_ROLES,
  FEMA_SOURCE_CONTEXT_KINDS,
  type FemaContextReceiptRole,
  type FemaSourceContextKind,
  type FemaSourceContextBinding,
  type FemaSourceReceipt,
  type RefdataFeature,
  type RefdataManifest,
} from '../../types/refdata';

function record(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function validReceipt(value: unknown): value is FemaSourceReceipt {
  if (!record(value)) return false;
  return typeof value.path === 'string' && value.path.startsWith('refdata/')
    && value.path.split('/').every(part => !!part && part !== '.' && part !== '..')
    && !/[\\?#\u0000-\u001f]/.test(value.path)
    && typeof value.generation === 'string' && /^[1-9]\d*$/.test(value.generation)
    && typeof value.sha256 === 'string' && /^[a-f0-9]{64}$/.test(value.sha256)
    && Number.isSafeInteger(value.bytes) && (value.bytes as number) > 0;
}

export function isFemaSourceContextBinding(value: unknown): value is FemaSourceContextBinding {
  if (!record(value) || value.schemaVersion !== 1 || value.datasetId !== 'fema-source-context'
    || typeof value.version !== 'string' || !value.version.trim() || !record(value.receipts)) return false;
  const receipts = value.receipts;
  return Object.keys(receipts).length === FEMA_CONTEXT_RECEIPT_ROLES.length
    && FEMA_CONTEXT_RECEIPT_ROLES.every(role => validReceipt(receipts[role]));
}

export function sameFemaSourceContextBinding(a: FemaSourceContextBinding, b: FemaSourceContextBinding): boolean {
  return a.version === b.version && a.schemaVersion === b.schemaVersion && a.datasetId === b.datasetId
    && FEMA_CONTEXT_RECEIPT_ROLES.every(role => {
      const left = a.receipts[role], right = b.receipts[role];
      return left.path === right.path && left.generation === right.generation
        && left.sha256 === right.sha256 && left.bytes === right.bytes;
    });
}

/** Legacy hazard snapshots remain readable; their missing companion cannot prove absence. */
export function validFemaContextManifest(manifest: Partial<RefdataManifest>): boolean {
  if (manifest.femaContext === undefined) return manifest.datasetId !== 'fema-source-context';
  return (manifest.datasetId === 'fema-nfhl' || manifest.datasetId === 'fema-source-context')
    && manifest.kind === 'vector' && isFemaSourceContextBinding(manifest.femaContext)
    && manifest.femaContext.version === manifest.version;
}

export const FEMA_CONTEXT_KIND_RECEIPT: Record<FemaSourceContextKind, FemaContextReceiptRole> = {
  'native-mapped': 'sourceAccounting',
  'native-unresolved': 'sourceAccounting',
  'native-unavailable': 'sourceAccounting',
  'qualified-legacy-sfha': 'legacyLineage',
  'legacy-unavailable': 'legacyLineage',
  'source-conflict': 'conflicts',
  'firm-panel': 'regulatoryContext',
  'political-area': 'regulatoryContext',
  lomr: 'regulatoryContext',
};

export type FemaSourceContextFeature = RefdataFeature & {
  props: RefdataFeature['props'] & {
    contextKind: FemaSourceContextKind;
    sourceFeatureId: string;
    sourceReceiptSha256: string;
  };
};

/**
 * Wire integrity only. Native topology / transformation certification belongs
 * to the immutable compiler receipts; downstream geometry assessment is still
 * required. Reject malformed masks before a spatial filter can discard them.
 */
export function isFemaSourceContextFeature(value: unknown, binding: FemaSourceContextBinding): value is FemaSourceContextFeature {
  if (!record(value) || typeof value.id !== 'string' || !value.id.trim()
    || !record(value.props) || !record(value.geom)) return false;
  const props = value.props;
  if (!(FEMA_SOURCE_CONTEXT_KINDS as readonly unknown[]).includes(props.contextKind)
    || typeof props.sourceFeatureId !== 'string' || !props.sourceFeatureId.trim()) return false;
  const kind = props.contextKind as FemaSourceContextKind;
  if (kind === 'legacy-unavailable' && (props.SOURCE_ROLE !== 'legacy-screening-derivative'
    || props.STATUS !== 'Source_Unavailable'
    || typeof props.sourceNativeFeatureSha256 !== 'string' || !/^[a-f0-9]{64}$/.test(props.sourceNativeFeatureSha256)
    || typeof props.sourceEnvelopeProofSha256 !== 'string' || !/^[a-f0-9]{64}$/.test(props.sourceEnvelopeProofSha256))) return false;
  if (props.sourceReceiptSha256 !== binding.receipts[FEMA_CONTEXT_KIND_RECEIPT[kind]].sha256) return false;
  const polygons = value.geom.type === 'Polygon' ? [value.geom.coordinates]
    : value.geom.type === 'MultiPolygon' ? value.geom.coordinates : null;
  if (!Array.isArray(polygons) || !polygons.length) return false;
  return polygons.every(poly => Array.isArray(poly) && poly.length > 0 && poly.every(ring => {
    if (!Array.isArray(ring) || ring.length < 4) return false;
    if (!ring.every(p => Array.isArray(p) && p.length === 2
      && Number.isFinite(p[0]) && Number.isFinite(p[1]) && Math.abs(p[0]) <= 180 && Math.abs(p[1]) <= 90)) return false;
    const points = ring as [number, number][];
    const first = points[0]!, last = points.at(-1)!;
    if (first[0] !== last[0] || first[1] !== last[1]) return false;
    const signedTwiceArea = points.slice(1).reduce((sum, p, index) => {
      const previous = points[index]!;
      return sum + (previous[0] - first[0]) * (p[1] - first[1])
        - (p[0] - first[0]) * (previous[1] - first[1]);
    }, 0);
    return Number.isFinite(signedTwiceArea) && Math.abs(signedTwiceArea) > 0;
  }));
}
